import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Command } from 'cmdk';
import type {
  DeviceConfigValues,
  DeviceType,
  PlayerClass,
  PlayerClassOverride,
  PlayerSummary,
} from '@displaycms/contracts';
import {
  CAPABILITY_FLAGS,
  DeviceTypes,
  Orientations,
  PLAYER_CLASS_PROFILES,
  PlayerClasses,
  capabilityLabel,
} from '@displaycms/contracts';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { StatusPill } from '@/components/ui/badge';
import { resolveStatus } from '@/lib/player-status';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { Icon, deviceTypeIcon, type IconName } from '@/components/icon';
import { InventoryMap } from '@/pages/players/inventory-map';
import { useAuth } from '@/auth/auth-context';
import { toast } from '@/lib/toast';
import {
  assignToPlayer,
  getPlayer,
  getPlayerAudit,
  getPlayerConfig,
  getPlayerTelemetry,
  issueDeviceCommand,
  listGroupsTree,
  overridePlayerClass,
  putPlayerConfig,
  revokePlayer,
  updatePlayer,
} from '@/api/players';
import { getCapabilityMatrix, listComposers } from '@/api/composers';
import { useComboboxDismiss } from '@/hooks/use-combobox-dismiss';
import { cn } from '@/lib/utils';
import { formatDate, formatBytes, type Locale } from '@/lib/format';
import { PlayerCommandControls } from '@/components/PlayerCommandControls';
import { ScreenshotViewerModal } from '@/components/ScreenshotViewerModal';
import { GroupCascader } from '@/components/players/group-cascader';

/**
 * `/players/:id` — player metadata, hardware profile, current
 * assignment, actions (rename / change group / revoke).
 *
 * 2026 detail-page pattern (SP4): a hero header (device-type icon + name +
 * identifier + status cluster + primary actions) over a two-column body —
 * a tabbed main column (Vue d'ensemble / Matériel / Cycle de vie / Commandes /
 * Position) and a sticky "Résumé" side rail with the secondary actions.
 *
 * NB — the tab panels are `forceMount`ed and hidden via
 * `data-[state=inactive]:hidden`. This keeps every panel in the DOM (the unit
 * tests query testids across panels without clicking a tab) while only the
 * active tab is visible.
 */

// Inactive Radix tab panels stay mounted (forceMount) but display:none.
const TAB_PANEL = 'space-y-4 data-[state=inactive]:hidden';

// Radix Select forbids empty-string item values — this sentinel stands in for
// the "unset / unchanged" option and is mapped back to '' in state.
const SELECT_NONE = '__none__';

export function PlayerDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { t, i18n: i18next } = useTranslation(['players', 'common']);
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const playerQuery = useQuery({
    queryKey: ['players', 'detail', id],
    queryFn: () => getPlayer(id!),
    enabled: typeof id === 'string' && id.length > 0,
    staleTime: 30_000,
  });

  // SP2 fast-follow: the screen's lifecycle history for the timeline.
  const auditQuery = useQuery({
    queryKey: ['players', 'audit', id],
    queryFn: () => getPlayerAudit(id!),
    enabled: typeof id === 'string' && id.length > 0,
    staleTime: 30_000,
  });

  const groupsQuery = useQuery({
    queryKey: ['groups', 'tree'],
    queryFn: () => listGroupsTree(),
    staleTime: 60_000,
  });

  const canUpdate = hasPermission('players.assign');
  const canRevoke = hasPermission('players.unpair');
  const canSupervise = hasPermission('players.supervise');
  const canEditConfig = hasPermission('players.assign');

  const [renameOpen, setRenameOpen] = useState<boolean>(false);
  const [renameDraft, setRenameDraft] = useState<string>('');
  const [groupOpen, setGroupOpen] = useState<boolean>(false);
  const [groupDraft, setGroupDraft] = useState<string>('');
  const [confirmRevoke, setConfirmRevoke] = useState<boolean>(false);
  const [overrideOpen, setOverrideOpen] = useState<boolean>(false);
  const [overrideClass, setOverrideClass] = useState<PlayerClass | ''>('');
  const [overrideFlags, setOverrideFlags] = useState<ReadonlySet<string>>(
    new Set(),
  );
  const [overrideReason, setOverrideReason] = useState<string>('');

  // P3 W2 B9 — assign affordance + capability-mismatch blocking modal.
  // The composer list is gated on `assignOpen` so it does NOT fire on
  // detail mount (keeps `players.test.tsx` untouched). The capability
  // matrix is consulted IMPERATIVELY in the submit handler — never via
  // `useQuery` on mount — for the same reason.
  const [assignOpen, setAssignOpen] = useState<boolean>(false);
  const [assignComposerId, setAssignComposerId] = useState<string>('');
  // UX audit — searchable typeahead for the (unbounded) composer list. Only the
  // picker widget changes; `assignComposerId` state + the assign/force mutation
  // flow are untouched.
  const [assignPickerOpen, setAssignPickerOpen] = useState<boolean>(false);
  const assignPickerRef = useRef<HTMLDivElement>(null);
  useComboboxDismiss(assignPickerRef, assignPickerOpen, () =>
    setAssignPickerOpen(false),
  );
  const [mismatchOpen, setMismatchOpen] = useState<boolean>(false);
  const [mismatchReason, setMismatchReason] = useState<string>('');
  const [pendingComposerId, setPendingComposerId] = useState<string>('');
  const [assignChecking, setAssignChecking] = useState<boolean>(false);

  // P5 W1 T9 — operator commands + screenshot viewer.
  const [screenshotCommandId, setScreenshotCommandId] = useState<string | null>(
    null,
  );

  // P4 W2 T4 — supervision panel state (reboot confirm dialog).
  const [rebootOpen, setRebootOpen] = useState<boolean>(false);
  const [rebootRationale, setRebootRationale] = useState<string>('');

  // P4 W3 T5 — per-device config editor state.
  const [configVolume, setConfigVolume] = useState<string>('');
  const [configOrientation, setConfigOrientation] = useState<string>('');
  const [configUpdateChannel, setConfigUpdateChannel] = useState<string>('');
  const [configDailyReboot, setConfigDailyReboot] = useState<string>('');
  const [configHeartbeat, setConfigHeartbeat] = useState<string>('');

  const telemetryQuery = useQuery({
    queryKey: ['players', 'telemetry', id],
    queryFn: () => getPlayerTelemetry(id!),
    enabled: typeof id === 'string' && id.length > 0,
    staleTime: 30_000,
  });

  const configQuery = useQuery({
    queryKey: ['players', 'config', id],
    queryFn: () => getPlayerConfig(id!),
    enabled: typeof id === 'string' && id.length > 0,
    staleTime: 30_000,
    // Populate local form state when the config loads.
    // We initialise from effective.values so inherited values are pre-filled.
    // The user only edits the device-scope fields; on save we build the body
    // from the current form state.
  });

  const composersQuery = useQuery({
    queryKey: ['composers', 'list', 'approved-for-assign'],
    queryFn: () => listComposers({ state: 'approved', per_page: 100 }),
    enabled: assignOpen,
    staleTime: 30_000,
  });

  // P4 W3 T5 — initialise config form from effective values when data loads.
  useEffect(() => {
    const eff = configQuery.data?.effective?.values;
    if (!eff) return;
    if (eff.volume_pct !== undefined) setConfigVolume(String(eff.volume_pct));
    if (eff.orientation !== undefined) setConfigOrientation(eff.orientation);
    if (eff.update_channel !== undefined) setConfigUpdateChannel(eff.update_channel);
    if (eff.daily_reboot_at !== undefined && eff.daily_reboot_at !== null)
      setConfigDailyReboot(eff.daily_reboot_at);
    if (eff.heartbeat_interval_s !== undefined)
      setConfigHeartbeat(String(eff.heartbeat_interval_s));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configQuery.data]);

  const renameMutation = useMutation({
    mutationFn: (display_name: string) => updatePlayer(id!, { display_name }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['players', 'detail', id], updated);
      void queryClient.invalidateQueries({ queryKey: ['players', 'list'] });
      setRenameOpen(false);
    },
  });

  const groupMutation = useMutation({
    mutationFn: (group_id: string | null) => updatePlayer(id!, { group_id }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['players', 'detail', id], updated);
      void queryClient.invalidateQueries({ queryKey: ['players', 'list'] });
      setGroupOpen(false);
    },
  });

  // SP2: set the screen TYPE (drives the type icon in the inventory).
  const deviceTypeMutation = useMutation({
    mutationFn: (device_type: DeviceType) => updatePlayer(id!, { device_type }),
    onSuccess: (updated) => {
      queryClient.setQueryData(['players', 'detail', id], updated);
      void queryClient.invalidateQueries({ queryKey: ['players', 'list'] });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: () => revokePlayer(id!),
    onSuccess: (updated) => {
      queryClient.setQueryData(['players', 'detail', id], updated);
      void queryClient.invalidateQueries({ queryKey: ['players', 'list'] });
      setConfirmRevoke(false);
    },
  });

  const overrideMutation = useMutation({
    mutationFn: (body: PlayerClassOverride) => overridePlayerClass(id!, body),
    onSuccess: (updated) => {
      queryClient.setQueryData(['players', 'detail', id], updated);
      void queryClient.invalidateQueries({ queryKey: ['players', 'list'] });
      void queryClient.invalidateQueries({
        queryKey: ['players', 'detail', id],
      });
      setOverrideOpen(false);
    },
  });

  // P3 W2 B9 — single assign mutation reused by both the compatible path
  // (no opts) and the force path (`{ force:true, reason }`). The wrapper's
  // conditional-spread guarantees the non-force wire body is byte-identical
  // to P1/P2 (`{ player_id, composer_id }`).
  type AssignVars =
    | { composerId: string }
    | { composerId: string; force: true; reason: string };
  const assignMutation = useMutation({
    mutationFn: (vars: AssignVars) => {
      if ('force' in vars) {
        return assignToPlayer(id!, vars.composerId, {
          force: true,
          reason: vars.reason,
        });
      }
      return assignToPlayer(id!, vars.composerId);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['players', 'detail', id],
      });
      void queryClient.invalidateQueries({ queryKey: ['players', 'list'] });
      setAssignOpen(false);
      setMismatchOpen(false);
      setAssignComposerId('');
      setPendingComposerId('');
      setMismatchReason('');
    },
  });

  // P4 W2 T4 — device command mutations (reboot + screenshot).
  const rebootMutation = useMutation({
    mutationFn: (rationale: string) =>
      issueDeviceCommand(id!, { command_type: 'device.reboot', rationale }),
    onSuccess: () => {
      setRebootOpen(false);
      setRebootRationale('');
      toast.success(t('players:detail.supervision.reboot_toast'));
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : String(err));
    },
  });

  const screenshotMutation = useMutation({
    mutationFn: () =>
      issueDeviceCommand(id!, { command_type: 'device.screenshot' }),
    onSuccess: () => {
      toast.success(t('players:detail.supervision.requested_toast'));
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : String(err));
    },
  });

  // P4 W3 T5 — config save mutation.
  const configMutation = useMutation({
    mutationFn: (values: DeviceConfigValues) => putPlayerConfig(id!, values),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['players', 'config', id] });
      toast.success(t('players:detail.config.saved_toast'));
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : t('players:detail.config.save_error'));
    },
  });

  const handleConfigSave = useCallback(() => {
    const values: DeviceConfigValues = {};
    const vol = Number(configVolume);
    if (configVolume !== '' && !Number.isNaN(vol)) values.volume_pct = vol;
    if ((Orientations as readonly string[]).includes(configOrientation))
      values.orientation = configOrientation as DeviceConfigValues['orientation'];
    if (configUpdateChannel === 'stable' || configUpdateChannel === 'beta')
      values.update_channel = configUpdateChannel;
    if (configDailyReboot !== '') values.daily_reboot_at = configDailyReboot;
    const hb = Number(configHeartbeat);
    if (configHeartbeat !== '' && !Number.isNaN(hb)) values.heartbeat_interval_s = hb;
    configMutation.mutate(values);
  }, [configVolume, configOrientation, configUpdateChannel, configDailyReboot, configHeartbeat, configMutation]);

  const handleStartRename = useCallback(() => {
    setRenameDraft(playerQuery.data?.display_name ?? '');
    setRenameOpen(true);
  }, [playerQuery.data?.display_name]);

  const handleStartChangeGroup = useCallback(() => {
    setGroupDraft(playerQuery.data?.group_id ?? '');
    setGroupOpen(true);
  }, [playerQuery.data?.group_id]);

  const handleStartOverride = useCallback(() => {
    setOverrideClass(playerQuery.data?.player_class ?? '');
    setOverrideFlags(
      new Set(playerQuery.data?.hardware_profile?.capability_flags ?? []),
    );
    setOverrideReason('');
    setOverrideOpen(true);
  }, [
    playerQuery.data?.player_class,
    playerQuery.data?.hardware_profile?.capability_flags,
  ]);

  const handleStartAssign = useCallback(() => {
    setAssignComposerId('');
    setMismatchReason('');
    setPendingComposerId('');
    setMismatchOpen(false);
    setAssignOpen(true);
  }, []);

  // FS §8.5 — submit-time matrix consult: for the chosen composer, look
  // up THIS player's row; resolved_version_id===null ⇒ mismatch (open the
  // blocking modal); non-null ⇒ compatible (call the byte-unchanged
  // non-force assignToPlayer path).
  const handleAssignSubmit = useCallback(async () => {
    if (!assignComposerId || assignChecking || !id) return;
    setAssignChecking(true);
    try {
      const matrix = await getCapabilityMatrix(assignComposerId);
      const row = matrix.players.find((p) => p.player_id === id);
      if (row && row.resolved_version_id !== null) {
        // Compatible — non-force, byte-identical wire body.
        assignMutation.mutate({ composerId: assignComposerId });
      } else {
        // Mismatch — open the blocking modal; DO NOT call assignToPlayer.
        setPendingComposerId(assignComposerId);
        setMismatchReason('');
        setMismatchOpen(true);
      }
    } finally {
      setAssignChecking(false);
    }
  }, [assignComposerId, assignChecking, id, assignMutation]);

  const handleForceAssign = useCallback(() => {
    const reason = mismatchReason.trim();
    if (reason.length < 3 || !pendingComposerId) return;
    assignMutation.mutate({
      composerId: pendingComposerId,
      force: true,
      reason,
    });
  }, [mismatchReason, pendingComposerId, assignMutation]);

  if (playerQuery.isLoading) {
    return (
      <p
        role="status"
        data-testid="detail-loading"
        className="text-sm text-muted-foreground"
      >
        {t('players:detail.loading')}
      </p>
    );
  }

  if (playerQuery.isError || !playerQuery.data) {
    return (
      <p role="alert" data-testid="detail-error" className="text-sm text-destructive">
        {t('players:detail.load_error')}
      </p>
    );
  }

  const player = playerQuery.data;
  const lang: Locale = i18next.language === 'en' ? 'en' : 'fr';

  // Override change-detection — mirrors the `PlayerClassOverride` contract
  // (reason ≥3 AND at least one of class/flags changed) so the client
  // never emits a body the server's zod schema would 400 on.
  const currentClass: PlayerClass | null = player.player_class;
  const currentFlags = new Set(
    player.hardware_profile?.capability_flags ?? [],
  );
  const classChanged = overrideClass !== '' && overrideClass !== currentClass;
  const flagsKey = (s: Iterable<string>): string =>
    [...s].sort().join('|');
  const flagsChanged =
    flagsKey(overrideFlags) !== flagsKey(currentFlags);
  const reasonValid = overrideReason.trim().length >= 3;
  const overrideValid =
    reasonValid && (classChanged || flagsChanged);

  const buildOverrideBody = (): PlayerClassOverride => {
    const body: PlayerClassOverride = { reason: overrideReason.trim() };
    if (classChanged) body.player_class = overrideClass;
    if (flagsChanged) body.capability_flags = [...overrideFlags];
    return body;
  };

  const toggleOverrideFlag = (flag: string): void => {
    setOverrideFlags((prev) => {
      const next = new Set(prev);
      if (next.has(flag)) next.delete(flag);
      else next.add(flag);
      return next;
    });
  };

  // --- Derived presentation values (hero + side rail, all testid-free) -------
  // Single source of truth shared with the players list (lib/player-status):
  // the resolved server status (running/idle/down/…), else the last-seen
  // derivation — so the detail and the list never render a different pill for
  // the same player. (Named `pairing` for historical reasons; it is the full
  // resolved status.)
  const pairing = resolveStatus(player, Date.now());
  const deviceTypeLabel = t(`players:device_type_${player.device_type ?? 'unknown'}`, {
    defaultValue: player.device_type ?? 'unknown',
  });
  const lastSeenLabel = player.last_seen_at
    ? formatDateTime(player.last_seen_at, lang)
    : t('players:card.last_seen_never');
  const groupLabel =
    (player.location_path ?? []).length > 0
      ? player.location_path!.join(' › ')
      : t('players:card.no_group');

  return (
    <div className="flex flex-col gap-6" data-testid="player-detail-page">
      {/* ---------------------------------------------------------------- *
       * HERO — device-type icon + name + identifier, status cluster, and
       * the primary action row (supervision / assign / planning).
       * ---------------------------------------------------------------- */}
      <header className="flex flex-col gap-4">
        <Link
          to="/players"
          className="inline-flex w-fit items-center gap-1 text-sm text-muted-foreground hover:underline"
          data-testid="detail-back"
        >
          <Icon name="chevron-left" size={16} aria-hidden />
          {t('players:detail.back')}
        </Link>

        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-start gap-4">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg border border-border bg-muted text-muted-foreground">
              <Icon name={deviceTypeIcon(player.device_type)} size={28} aria-hidden />
            </span>
            <div className="flex flex-col gap-1">
              <h1
                className="text-2xl font-semibold tracking-tight"
                data-testid="detail-name"
              >
                {player.display_name}
              </h1>
              <code className="font-mono text-xs text-muted-foreground">
                {player.id}
              </code>
              <span className="text-sm text-muted-foreground">
                {deviceTypeLabel}
              </span>
            </div>
          </div>

          {/* Status cluster (testid-free; canonical pills live in the tabs) */}
          <div className="flex flex-col items-start gap-2 lg:items-end">
            <div className="flex flex-wrap items-center gap-2">
              <StatusPill
                status={pairing}
                label={t(`players:status_badge.${pairing}`, { defaultValue: pairing })}
              />
              <StatusPill
                status={player.lifecycle_status ?? 'active'}
                label={t(`players:lifecycle_${player.lifecycle_status ?? 'active'}`, {
                  defaultValue: player.lifecycle_status ?? 'active',
                })}
              />
            </div>
            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              <Icon name="clock" size={14} aria-hidden />
              {t('players:detail.label_last_seen')}: {lastSeenLabel}
            </span>
          </div>
        </div>

        {/* Primary actions row */}
        <div className="flex flex-wrap items-center gap-2">
          {canSupervise && (
            <div className="flex flex-wrap items-center gap-2" data-testid="supervision-panel">
              <Button
                type="button"
                variant="destructive"
                size="sm"
                className="gap-2"
                data-testid="supervision-reboot"
                disabled={rebootMutation.isPending}
                onClick={() => {
                  setRebootRationale('');
                  setRebootOpen(true);
                }}
              >
                <Icon name="power" size={16} aria-hidden />
                {t('players:detail.supervision.reboot')}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-2"
                data-testid="supervision-screenshot"
                disabled={screenshotMutation.isPending}
                onClick={() => void screenshotMutation.mutate()}
              >
                <Icon name="image" size={16} aria-hidden />
                {t('players:detail.supervision.screenshot')}
              </Button>
            </div>
          )}
          {canUpdate && (
            <Button
              type="button"
              variant="default"
              size="sm"
              className="gap-2"
              onClick={handleStartAssign}
              data-testid="player-assign-open"
            >
              <Icon name="layers" size={16} aria-hidden />
              {t('players:assign.open')}
            </Button>
          )}
          <Link
            to={`/players/${player.id}/planning`}
            data-testid="planning-link"
            className="inline-flex h-9 items-center gap-2 rounded-md border border-input bg-background px-3 text-sm font-medium hover:bg-accent hover:text-accent-foreground"
          >
            <Icon name="calendar" size={16} aria-hidden />
            {t('players:detail.planning_link', { defaultValue: 'Planification du lecteur' })}
          </Link>
        </div>
      </header>

      {/* ---------------------------------------------------------------- *
       * BODY — tabbed main column + sticky "Résumé" side rail.
       * ---------------------------------------------------------------- */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <main className="lg:col-span-2">
          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="flex h-auto flex-wrap justify-start gap-1">
              <TabsTrigger value="overview" data-testid="tab-overview" className="gap-1.5">
                <Icon name="info" size={14} aria-hidden />
                {t('players:detail.tabs.overview', { defaultValue: "Vue d'ensemble" })}
              </TabsTrigger>
              <TabsTrigger value="hardware" data-testid="tab-hardware" className="gap-1.5">
                <Icon name="monitor" size={14} aria-hidden />
                {t('players:detail.tabs.hardware', { defaultValue: 'Matériel' })}
              </TabsTrigger>
              <TabsTrigger value="lifecycle" data-testid="tab-lifecycle" className="gap-1.5">
                <Icon name="clock" size={14} aria-hidden />
                {t('players:detail.tabs.lifecycle', { defaultValue: 'Cycle de vie' })}
              </TabsTrigger>
              <TabsTrigger value="commands" data-testid="tab-commands" className="gap-1.5">
                <Icon name="settings" size={14} aria-hidden />
                {t('players:detail.tabs.commands', { defaultValue: 'Commandes' })}
              </TabsTrigger>
              <TabsTrigger value="position" data-testid="tab-position" className="gap-1.5">
                <Icon name="map-pin" size={14} aria-hidden />
                {t('players:detail.tabs.position', { defaultValue: 'Position' })}
              </TabsTrigger>
            </TabsList>

            {/* --- Vue d'ensemble: metadata + screen + current item + telemetry --- */}
            <TabsContent value="overview" forceMount className={TAB_PANEL}>
              <Card aria-labelledby="meta-heading">
                <SectionTitle id="meta-heading" icon="info">
                  {t('players:detail.metadata_heading')}
                </SectionTitle>
                <CardContent>
                  <dl
                    className="grid grid-cols-1 gap-x-4 gap-y-2 text-sm sm:grid-cols-2"
                    data-testid="detail-meta"
                  >
                    <MetaRow label={t('players:detail.label_id')} value={player.id} />
                    <MetaRow
                      label={t('players:detail.label_group')}
                      value={groupLabel}
                      testId="detail-location"
                    />
                    <MetaRow
                      label={t('players:detail.label_state')}
                      value={t(`players:status_badge.${pairing}`)}
                      testId="detail-state"
                    />
                    <MetaRow
                      label={t('players:detail.label_last_seen')}
                      value={lastSeenLabel}
                      testId="detail-last-seen"
                    />
                    <MetaRow
                      label={t('players:detail.label_created_at')}
                      value={formatDateTime(player.created_at, lang)}
                    />
                  </dl>
                </CardContent>
              </Card>

              <Card aria-labelledby="screen-heading" data-testid="detail-screen">
                <SectionTitle id="screen-heading" icon={deviceTypeIcon(player.device_type)}>
                  {t('players:detail.screen_heading', { defaultValue: 'Écran' })}
                </SectionTitle>
                <CardContent>
                  <dl className="grid grid-cols-1 gap-x-4 gap-y-2 text-sm sm:grid-cols-2">
                    <div className="flex items-center justify-between gap-2">
                      <dt className="text-muted-foreground">
                        {t('players:detail.label_device_type', { defaultValue: 'Type d’écran' })}
                      </dt>
                      <dd className="flex items-center gap-1.5" data-testid="detail-device-type">
                        <Icon name={deviceTypeIcon(player.device_type)} size={16} />
                        <Select
                          value={player.device_type ?? 'unknown'}
                          onValueChange={(v) => deviceTypeMutation.mutate(v as DeviceType)}
                        >
                          <SelectTrigger
                            className="h-8 w-40"
                            aria-label={t('players:detail.label_device_type', {
                              defaultValue: 'Type d’écran',
                            })}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {DeviceTypes.map((dt) => (
                              <SelectItem key={dt} value={dt}>
                                {t(`players:device_type_${dt}`, { defaultValue: dt })}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </dd>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <dt className="text-muted-foreground">
                        {t('players:detail.label_status', { defaultValue: 'Statut' })}
                      </dt>
                      <dd data-testid="detail-status">
                        <StatusPill
                          status={pairing}
                          label={t(`players:status_badge.${pairing}`, {
                            defaultValue: pairing,
                          })}
                        />
                      </dd>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <dt className="text-muted-foreground">
                        {t('players:detail.label_lifecycle', { defaultValue: 'Cycle de vie' })}
                      </dt>
                      <dd data-testid="detail-lifecycle">
                        {t(`players:lifecycle_${player.lifecycle_status ?? 'active'}`, {
                          defaultValue: player.lifecycle_status ?? 'active',
                        })}
                      </dd>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <dt className="text-muted-foreground">
                        {t('players:detail.label_gps', { defaultValue: 'Position GPS' })}
                      </dt>
                      <dd data-testid="detail-gps">
                        {player.gps
                          ? `${player.gps.lat.toFixed(5)}, ${player.gps.lng.toFixed(5)} · ${t(
                              `players:gps_source_${player.gps.source}`,
                              { defaultValue: player.gps.source },
                            )}`
                          : t('players:detail.gps_none', { defaultValue: 'Non positionné' })}
                      </dd>
                    </div>
                  </dl>
                </CardContent>
              </Card>

              <Card aria-labelledby="current-heading">
                <SectionTitle id="current-heading" icon="play-circle">
                  {t('players:detail.current_item_heading')}
                </SectionTitle>
                <CardContent>
                  {player.last_status ? (
                    <dl
                      className="grid grid-cols-1 gap-x-4 gap-y-2 text-sm sm:grid-cols-2"
                      data-testid="detail-current-status"
                    >
                      <MetaRow
                        label={t('players:detail.label_state')}
                        value={
                          player.last_status.current_resource_version_id ??
                          t('players:card.current_item_none')
                        }
                      />
                      <MetaRow
                        label={t('players:detail.label_online_since')}
                        value={
                          player.last_status.online_since
                            ? formatDateTime(player.last_status.online_since, lang)
                            : t('players:card.last_seen_never')
                        }
                      />
                    </dl>
                  ) : (
                    <p className="text-sm text-muted-foreground" data-testid="detail-no-status">
                      {t('players:detail.no_status')}
                    </p>
                  )}
                </CardContent>
              </Card>

              <Card aria-labelledby="telemetry-heading">
                <SectionTitle id="telemetry-heading" icon="refresh-cw">
                  {t('players:detail.telemetry_heading')}
                </SectionTitle>
                <CardContent>
                  {telemetryQuery.data?.latest ? (
                    <dl
                      className="grid grid-cols-1 gap-x-4 gap-y-2 text-sm sm:grid-cols-2"
                      data-testid="player-telemetry-panel"
                    >
                      <div className="flex flex-col">
                        <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                          {t('players:detail.telemetry.battery')}
                        </dt>
                        <dd className="text-sm" data-testid="telemetry-battery">
                          {telemetryQuery.data.latest.battery_pct != null
                            ? String(telemetryQuery.data.latest.battery_pct)
                            : '—'}
                        </dd>
                      </div>
                      <div className="flex flex-col">
                        <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                          {t('players:detail.telemetry.net')}
                        </dt>
                        <dd className="text-sm" data-testid="telemetry-net">
                          {telemetryQuery.data.latest.net_type}
                        </dd>
                      </div>
                      <div className="flex flex-col">
                        <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                          {t('players:detail.telemetry.uptime')}
                        </dt>
                        <dd className="text-sm" data-testid="telemetry-uptime">
                          {String(telemetryQuery.data.latest.uptime_s)}
                        </dd>
                      </div>
                      <div className="flex flex-col">
                        <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                          {t('players:detail.telemetry.versions')}
                        </dt>
                        <dd className="text-sm" data-testid="telemetry-versions">
                          {telemetryQuery.data.latest.app_version} / {telemetryQuery.data.latest.wrapper_version}
                        </dd>
                      </div>
                      <div className="flex flex-col">
                        <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                          {t('players:detail.telemetry.kiosk_level')}
                        </dt>
                        <dd className="text-sm" data-testid="telemetry-kiosk-level">
                          {telemetryQuery.data.latest.kiosk_level}
                        </dd>
                      </div>
                      <div className="flex flex-col">
                        <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                          {t('players:detail.telemetry.last_seen')}
                        </dt>
                        <dd className="text-sm">
                          {formatDateTime(telemetryQuery.data.latest.ts, lang)}
                        </dd>
                      </div>
                    </dl>
                  ) : (
                    <p
                      className="text-sm text-muted-foreground"
                      data-testid="telemetry-empty"
                    >
                      {t('players:detail.telemetry.empty')}
                    </p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* --- Matériel: hardware profile --- */}
            <TabsContent value="hardware" forceMount className={TAB_PANEL}>
              <Card aria-labelledby="hw-heading">
                <SectionTitle id="hw-heading" icon="settings">
                  {t('players:detail.hardware_heading')}
                </SectionTitle>
                <CardContent>
                  <dl
                    className="grid grid-cols-1 gap-x-4 gap-y-2 text-sm sm:grid-cols-2"
                    data-testid="detail-hardware"
                  >
                    <MetaRow
                      label={t('players:detail.hw_user_agent')}
                      value={player.hardware_profile?.user_agent ?? t('players:detail.hw_unknown')}
                      testId="hw-user-agent"
                    />
                    <MetaRow
                      label={t('players:detail.hw_screen')}
                      value={
                        player.hardware_profile?.screen_width && player.hardware_profile.screen_height
                          ? `${player.hardware_profile.screen_width}×${player.hardware_profile.screen_height}`
                          : t('players:detail.hw_unknown')
                      }
                      testId="hw-screen"
                    />
                    <MetaRow
                      label={t('players:detail.hw_dpr')}
                      value={
                        player.hardware_profile?.device_pixel_ratio
                          ? String(player.hardware_profile.device_pixel_ratio)
                          : t('players:detail.hw_unknown')
                      }
                    />
                    <MetaRow
                      label={t('players:detail.hw_locale')}
                      value={player.hardware_profile?.locale ?? t('players:detail.hw_unknown')}
                    />
                    <MetaRow
                      label={t('players:detail.hw_timezone')}
                      value={player.hardware_profile?.timezone ?? t('players:detail.hw_unknown')}
                    />
                    <MetaRow
                      label={t('players:detail.hw_storage')}
                      value={
                        player.hardware_profile?.storage_quota_bytes
                          ? formatBytes(player.hardware_profile.storage_quota_bytes, lang)
                          : t('players:detail.hw_unknown')
                      }
                    />
                  </dl>
                </CardContent>
              </Card>
            </TabsContent>

            {/* --- Cycle de vie: lifecycle history timeline --- */}
            <TabsContent value="lifecycle" forceMount className={TAB_PANEL}>
              <Card aria-labelledby="timeline-heading" data-testid="detail-lifecycle-timeline">
                <SectionTitle id="timeline-heading" icon="clock">
                  {t('players:detail.timeline_heading', { defaultValue: 'Historique du cycle de vie' })}
                </SectionTitle>
                <CardContent>
                  {auditQuery.data && auditQuery.data.length > 0 ? (
                    <ol className="space-y-2 text-sm" data-testid="lifecycle-timeline-list">
                      {auditQuery.data.map((e, i) => (
                        <li
                          key={`${e.at}-${i}`}
                          className="flex items-start justify-between gap-2 border-l-2 border-border pl-3"
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-1.5">
                              <StatusPill
                                status={e.to}
                                label={t(`players:lifecycle_${e.to}`, { defaultValue: e.to })}
                              />
                              <span className="text-xs text-muted-foreground">
                                ← {t(`players:lifecycle_${e.from}`, { defaultValue: e.from })}
                              </span>
                            </div>
                            {e.reason && <p className="mt-0.5 text-muted-foreground">{e.reason}</p>}
                          </div>
                          <time className="whitespace-nowrap text-xs text-muted-foreground">
                            {formatDate(e.at, lang)}
                          </time>
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      {t('players:detail.timeline_empty', {
                        defaultValue: 'Aucun changement de cycle de vie',
                      })}
                    </p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* --- Commandes: operator commands + per-device config --- */}
            <TabsContent value="commands" forceMount className={TAB_PANEL}>
              <PlayerCommandControls
                playerId={player.id}
                onScreenshotReady={(commandId) => setScreenshotCommandId(commandId)}
              />

              {/* P4 W3 T5 — per-device config editor (visible for players.read; save gated by players.assign) */}
              <Card aria-labelledby="config-heading" data-testid="config-editor">
                <SectionTitle id="config-heading" icon="settings">
                  {t('players:detail.config.heading')}
                </SectionTitle>
                <CardContent>
                  <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
                    {/* Volume */}
                    <div className="flex flex-col gap-1">
                      <label htmlFor="config-volume-input" className="text-xs uppercase tracking-wide text-muted-foreground">
                        {t('players:detail.config.volume')}
                      </label>
                      <div className="flex items-center gap-2">
                        <Input
                          id="config-volume-input"
                          type="number"
                          min={0}
                          max={100}
                          value={configVolume}
                          onChange={(e) => setConfigVolume(e.target.value)}
                          disabled={!canEditConfig || configMutation.isPending}
                          data-testid="config-volume"
                        />
                        {(() => {
                          const deviceVals = configQuery.data?.device?.values ?? {};
                          const effVals = configQuery.data?.effective?.values ?? {};
                          const isInherited = 'volume_pct' in effVals && !('volume_pct' in deviceVals);
                          return isInherited ? (
                            <span className="text-xs text-muted-foreground" data-testid="config-volume-inherited">
                              {t('players:detail.config.inherited')}
                            </span>
                          ) : null;
                        })()}
                      </div>
                    </div>

                    {/* Orientation */}
                    <div className="flex flex-col gap-1">
                      <label htmlFor="config-orientation-select" className="text-xs uppercase tracking-wide text-muted-foreground">
                        {t('players:detail.config.orientation')}
                      </label>
                      <div className="flex items-center gap-2">
                        <Select
                          value={configOrientation === '' ? SELECT_NONE : configOrientation}
                          onValueChange={(v) => setConfigOrientation(v === SELECT_NONE ? '' : v)}
                          disabled={!canEditConfig || configMutation.isPending}
                        >
                          <SelectTrigger
                            id="config-orientation-select"
                            data-testid="config-orientation"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={SELECT_NONE}>—</SelectItem>
                            {Orientations.map((o) => (
                              <SelectItem key={o} value={o}>
                                {t(`players:detail.config.orientation_${o}`)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {(() => {
                          const deviceVals = configQuery.data?.device?.values ?? {};
                          const effVals = configQuery.data?.effective?.values ?? {};
                          const isInherited = 'orientation' in effVals && !('orientation' in deviceVals);
                          return isInherited ? (
                            <span className="text-xs text-muted-foreground" data-testid="config-orientation-inherited">
                              {t('players:detail.config.inherited')}
                            </span>
                          ) : null;
                        })()}
                      </div>
                    </div>

                    {/* Update channel */}
                    <div className="flex flex-col gap-1">
                      <label htmlFor="config-update-channel-select" className="text-xs uppercase tracking-wide text-muted-foreground">
                        {t('players:detail.config.update_channel')}
                      </label>
                      <div className="flex items-center gap-2">
                        <Select
                          value={configUpdateChannel === '' ? SELECT_NONE : configUpdateChannel}
                          onValueChange={(v) => setConfigUpdateChannel(v === SELECT_NONE ? '' : v)}
                          disabled={!canEditConfig || configMutation.isPending}
                        >
                          <SelectTrigger
                            id="config-update-channel-select"
                            data-testid="config-update-channel"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={SELECT_NONE}>—</SelectItem>
                            <SelectItem value="stable">
                              {t('players:detail.config.channel_stable')}
                            </SelectItem>
                            <SelectItem value="beta">
                              {t('players:detail.config.channel_beta')}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        {(() => {
                          const deviceVals = configQuery.data?.device?.values ?? {};
                          const effVals = configQuery.data?.effective?.values ?? {};
                          const isInherited = 'update_channel' in effVals && !('update_channel' in deviceVals);
                          return isInherited ? (
                            <span className="text-xs text-muted-foreground" data-testid="config-update-channel-inherited">
                              {t('players:detail.config.inherited')}
                            </span>
                          ) : null;
                        })()}
                      </div>
                    </div>

                    {/* Daily reboot */}
                    <div className="flex flex-col gap-1">
                      <label htmlFor="config-daily-reboot-input" className="text-xs uppercase tracking-wide text-muted-foreground">
                        {t('players:detail.config.daily_reboot')}
                      </label>
                      <div className="flex items-center gap-2">
                        <Input
                          id="config-daily-reboot-input"
                          type="text"
                          placeholder="HH:MM"
                          value={configDailyReboot}
                          onChange={(e) => setConfigDailyReboot(e.target.value)}
                          disabled={!canEditConfig || configMutation.isPending}
                          data-testid="config-daily-reboot"
                        />
                        {(() => {
                          const deviceVals = configQuery.data?.device?.values ?? {};
                          const effVals = configQuery.data?.effective?.values ?? {};
                          const isInherited = 'daily_reboot_at' in effVals && !('daily_reboot_at' in deviceVals);
                          return isInherited ? (
                            <span className="text-xs text-muted-foreground" data-testid="config-daily-reboot-inherited">
                              {t('players:detail.config.inherited')}
                            </span>
                          ) : null;
                        })()}
                      </div>
                    </div>

                    {/* Heartbeat interval */}
                    <div className="flex flex-col gap-1">
                      <label htmlFor="config-heartbeat-input" className="text-xs uppercase tracking-wide text-muted-foreground">
                        {t('players:detail.config.heartbeat')}
                      </label>
                      <div className="flex items-center gap-2">
                        <Input
                          id="config-heartbeat-input"
                          type="number"
                          min={15}
                          max={3600}
                          value={configHeartbeat}
                          onChange={(e) => setConfigHeartbeat(e.target.value)}
                          disabled={!canEditConfig || configMutation.isPending}
                          data-testid="config-heartbeat"
                        />
                        {(() => {
                          const deviceVals = configQuery.data?.device?.values ?? {};
                          const effVals = configQuery.data?.effective?.values ?? {};
                          const isInherited = 'heartbeat_interval_s' in effVals && !('heartbeat_interval_s' in deviceVals);
                          return isInherited ? (
                            <span className="text-xs text-muted-foreground" data-testid="config-heartbeat-inherited">
                              {t('players:detail.config.inherited')}
                            </span>
                          ) : null;
                        })()}
                      </div>
                    </div>
                  </div>

                  {canEditConfig && (
                    <div className="mt-4">
                      <Button
                        type="button"
                        className="gap-2"
                        disabled={configMutation.isPending}
                        onClick={handleConfigSave}
                        data-testid="config-save"
                      >
                        <Icon name="check" size={16} aria-hidden />
                        {t('players:detail.config.save')}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* --- Position: GPS / inventory map --- *
             * NB — NOT forceMount'd (unlike the other tabs): Leaflet's
             * MapContainer must initialise while visible or it paints at zero
             * size. Mirrors the shipped list.tsx map tab. The only testid here
             * (detail-minimap) is asserted solely for ABSENCE under gps-null,
             * which still holds when the inactive tab is unmounted. */}
            <TabsContent value="position" className={TAB_PANEL}>
              <Card aria-labelledby="position-heading">
                <SectionTitle id="position-heading" icon="map-pin">
                  {t('players:detail.label_gps', { defaultValue: 'Position GPS' })}
                </SectionTitle>
                <CardContent>
                  {player.gps ? (
                    <div data-testid="detail-minimap">
                      <InventoryMap players={[player]} lang={lang} />
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      {t('players:detail.gps_none', { defaultValue: 'Non positionné' })}
                    </p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </main>

        {/* --- Side rail: "Résumé" summary + secondary actions --- */}
        <aside className="lg:col-span-1">
          <Card className="lg:sticky lg:top-6">
            <SectionTitle id="summary-heading" icon="info">
              {t('players:detail.summary_heading', { defaultValue: 'Résumé' })}
            </SectionTitle>
            <CardContent className="flex flex-col gap-4">
              {/* Status pills (testid-free copies) */}
              <div className="flex flex-wrap items-center gap-2">
                <StatusPill
                  status={pairing}
                  label={t(`players:status_badge.${pairing}`, { defaultValue: pairing })}
                />
                <StatusPill
                  status={player.lifecycle_status ?? 'active'}
                  label={t(`players:lifecycle_${player.lifecycle_status ?? 'active'}`, {
                    defaultValue: player.lifecycle_status ?? 'active',
                  })}
                />
              </div>

              <dl className="flex flex-col gap-2 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <dt className="flex items-center gap-1.5 text-muted-foreground">
                    <Icon name="building-2" size={14} aria-hidden />
                    {t('players:detail.label_group')}
                  </dt>
                  <dd className="text-right">{groupLabel}</dd>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <dt className="flex items-center gap-1.5 text-muted-foreground">
                    <Icon name="clock" size={14} aria-hidden />
                    {t('players:detail.label_last_seen')}
                  </dt>
                  <dd className="text-right">{lastSeenLabel}</dd>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <dt className="flex items-center gap-1.5 text-muted-foreground">
                    <Icon name="map-pin" size={14} aria-hidden />
                    {t('players:detail.label_gps')}
                  </dt>
                  <dd className="text-right">
                    {player.gps
                      ? `${player.gps.lat.toFixed(3)}, ${player.gps.lng.toFixed(3)}`
                      : t('players:detail.gps_none', { defaultValue: 'Non positionné' })}
                  </dd>
                </div>
              </dl>

              {/* Secondary / destructive actions */}
              <div className="flex flex-col gap-2 border-t border-border pt-4" data-testid="detail-actions">
                {canUpdate && !renameOpen && (
                  <Button
                    type="button"
                    variant="outline"
                    className="justify-start gap-2"
                    onClick={handleStartRename}
                    data-testid="action-rename"
                  >
                    <Icon name="settings" size={16} aria-hidden />
                    {t('players:detail.rename')}
                  </Button>
                )}
                {canUpdate && !groupOpen && (
                  <Button
                    type="button"
                    variant="outline"
                    className="justify-start gap-2"
                    onClick={handleStartChangeGroup}
                    data-testid="action-change-group"
                  >
                    <Icon name="building-2" size={16} aria-hidden />
                    {t('players:detail.change_group')}
                  </Button>
                )}
                {canUpdate && (
                  <Button
                    type="button"
                    variant="outline"
                    className="justify-start gap-2"
                    onClick={handleStartOverride}
                    data-testid="player-override-open"
                  >
                    <Icon name="wrench" size={16} aria-hidden />
                    {t('players:override.open')}
                  </Button>
                )}
                {canRevoke && player.pairing_state !== 'revoked' && (
                  <Button
                    type="button"
                    variant="destructive"
                    className="justify-start gap-2"
                    onClick={() => setConfirmRevoke(true)}
                    data-testid="action-revoke"
                  >
                    <Icon name="trash-2" size={16} aria-hidden />
                    {t('players:detail.revoke')}
                  </Button>
                )}

                {renameOpen && (
                  <form
                    className="mt-1 flex flex-col gap-2"
                    data-testid="rename-form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (renameDraft.trim() === '') return;
                      renameMutation.mutate(renameDraft.trim());
                    }}
                  >
                    <label className="flex flex-col gap-1 text-sm">
                      <span className="font-medium">{t('players:detail.rename')}</span>
                      <Input
                        type="text"
                        required
                        value={renameDraft}
                        onChange={(e) => setRenameDraft(e.target.value)}
                        disabled={renameMutation.isPending}
                        data-testid="rename-input"
                      />
                    </label>
                    <div className="flex gap-2">
                      <Button
                        type="submit"
                        disabled={renameDraft.trim() === '' || renameMutation.isPending}
                        data-testid="rename-save"
                      >
                        {t('players:detail.save')}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setRenameOpen(false)}
                        disabled={renameMutation.isPending}
                        data-testid="rename-cancel"
                      >
                        {t('players:detail.cancel')}
                      </Button>
                    </div>
                  </form>
                )}

                {groupOpen && (
                  <form
                    className="mt-1 flex flex-col gap-2"
                    data-testid="group-form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      groupMutation.mutate(groupDraft === '' ? null : groupDraft);
                    }}
                  >
                    <div className="flex flex-col gap-1 text-sm">
                      <span className="font-medium">{t('players:detail.change_group')}</span>
                      <GroupCascader
                        tree={groupsQuery.data?.tree ?? []}
                        value={groupDraft || null}
                        onChange={(gid) => setGroupDraft(gid ?? '')}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="submit"
                        disabled={groupMutation.isPending}
                        data-testid="group-save"
                      >
                        {t('players:detail.save')}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setGroupOpen(false)}
                        disabled={groupMutation.isPending}
                        data-testid="group-cancel"
                      >
                        {t('players:detail.cancel')}
                      </Button>
                    </div>
                  </form>
                )}
              </div>
            </CardContent>
          </Card>
        </aside>
      </div>

      {/* ---------------------------------------------------------------- *
       * Modals + overlays (rendered at the page root).
       * ---------------------------------------------------------------- */}
      {rebootOpen && (
        <div
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="reboot-title"
          data-testid="supervision-reboot-dialog"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
        >
          <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-lg">
            <h2 id="reboot-title" className="text-lg font-semibold">
              {t('players:detail.supervision.reboot_confirm_title')}
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              {t('players:detail.supervision.reboot_confirm_body')}
            </p>
            <div className="mt-4 flex flex-col gap-2">
              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium">
                  {t('players:detail.supervision.reboot_rationale_label')}
                </span>
                <textarea
                  className="min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={rebootRationale}
                  onChange={(e) => setRebootRationale(e.target.value)}
                  disabled={rebootMutation.isPending}
                  data-testid="supervision-reboot-rationale"
                />
              </label>
            </div>
            <footer className="mt-4 flex items-center justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setRebootOpen(false)}
                disabled={rebootMutation.isPending}
                data-testid="supervision-reboot-cancel"
              >
                {t('players:detail.supervision.cancel')}
              </Button>
              <Button
                type="button"
                variant="destructive"
                disabled={rebootRationale.trim() === '' || rebootMutation.isPending}
                onClick={() => void rebootMutation.mutate(rebootRationale)}
                data-testid="supervision-reboot-confirm"
              >
                {t('players:detail.supervision.reboot_confirm')}
              </Button>
            </footer>
          </div>
        </div>
      )}

      <ScreenshotViewerModal
        commandId={screenshotCommandId}
        playerId={player.id}
        onClose={() => setScreenshotCommandId(null)}
      />

      {confirmRevoke && (
        <div
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="revoke-title"
          data-testid="revoke-confirm"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
        >
          <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-lg">
            <h2 id="revoke-title" className="text-lg font-semibold">
              {t('players:detail.revoke_confirm_title')}
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              {t('players:detail.revoke_confirm_body')}
            </p>
            <footer className="mt-4 flex items-center justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setConfirmRevoke(false)}
                disabled={revokeMutation.isPending}
                data-testid="revoke-cancel"
              >
                {t('players:detail.revoke_confirm_cancel')}
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={() => revokeMutation.mutate()}
                disabled={revokeMutation.isPending}
                data-testid="revoke-confirm-ok"
              >
                {t('players:detail.revoke_confirm_ok')}
              </Button>
            </footer>
          </div>
        </div>
      )}

      {overrideOpen && (
        <div
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="override-title"
          data-testid="player-override-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
        >
          <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-lg">
            <h2 id="override-title" className="text-lg font-semibold">
              {t('players:override.title')}
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              {t('players:override.body')}
            </p>
            <form
              className="mt-4 flex flex-col gap-4"
              onSubmit={(e) => {
                e.preventDefault();
                if (!overrideValid || overrideMutation.isPending) return;
                overrideMutation.mutate(buildOverrideBody());
              }}
            >
              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium">
                  {t('players:override.class_label')}
                </span>
                <Select
                  value={overrideClass === '' ? SELECT_NONE : overrideClass}
                  onValueChange={(v) =>
                    setOverrideClass(v === SELECT_NONE ? '' : (v as PlayerClass))
                  }
                  disabled={overrideMutation.isPending}
                >
                  <SelectTrigger data-testid="player-override-class">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={SELECT_NONE}>
                      {t('players:override.class_unchanged')}
                    </SelectItem>
                    {PlayerClasses.map((c) => (
                      <SelectItem key={c} value={c}>
                        {PLAYER_CLASS_PROFILES[c].label_i18n[lang]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </label>

              <fieldset className="flex flex-col gap-2 text-sm">
                <legend className="font-medium">
                  {t('players:override.flags_label')}
                </legend>
                <div className="flex flex-col gap-1">
                  {CAPABILITY_FLAGS.map((cf) => (
                    <label
                      key={cf.flag}
                      className="flex items-center gap-2"
                    >
                      <input
                        type="checkbox"
                        checked={overrideFlags.has(cf.flag)}
                        onChange={() => toggleOverrideFlag(cf.flag)}
                        disabled={overrideMutation.isPending}
                        data-testid={`player-override-flag-${cf.flag}`}
                      />
                      <span>{capabilityLabel(cf.flag, lang)}</span>
                    </label>
                  ))}
                </div>
              </fieldset>

              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium">
                  {t('players:override.reason_label')}
                </span>
                <Input
                  type="text"
                  required
                  minLength={3}
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e.target.value)}
                  placeholder={t('players:override.reason_placeholder')}
                  disabled={overrideMutation.isPending}
                  data-testid="player-override-reason"
                />
              </label>

              <footer className="mt-2 flex items-center justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setOverrideOpen(false)}
                  disabled={overrideMutation.isPending}
                  data-testid="player-override-cancel"
                >
                  {t('players:override.cancel')}
                </Button>
                <Button
                  type="submit"
                  disabled={!overrideValid || overrideMutation.isPending}
                  data-testid="player-override-submit"
                >
                  {t('players:override.submit')}
                </Button>
              </footer>
            </form>
          </div>
        </div>
      )}

      {assignOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="assign-title"
          data-testid="player-assign-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
        >
          <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-lg">
            <h2 id="assign-title" className="text-lg font-semibold">
              {t('players:assign.modal_title')}
            </h2>
            <form
              className="mt-4 flex flex-col gap-4"
              onSubmit={(e) => {
                e.preventDefault();
                void handleAssignSubmit();
              }}
            >
              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium">
                  {t('players:assign.composer_label')}
                </span>
                <div className="relative" ref={assignPickerRef}>
                  <button
                    type="button"
                    role="combobox"
                    aria-expanded={assignPickerOpen}
                    disabled={assignMutation.isPending || assignChecking}
                    onClick={() => setAssignPickerOpen((o) => !o)}
                    data-testid="player-assign-composer-select"
                    className={cn(
                      'flex h-10 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 text-sm',
                      'ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
                      'disabled:cursor-not-allowed disabled:opacity-50',
                    )}
                  >
                    <span
                      className={cn(
                        'truncate text-left',
                        assignComposerId === '' && 'text-muted-foreground',
                      )}
                    >
                      {(() => {
                        const sel = (composersQuery.data?.items ?? []).find(
                          (c) => c.id === assignComposerId,
                        );
                        if (!sel) return t('players:assign.composer_placeholder');
                        return lang === 'en' && sel.name_i18n.en
                          ? sel.name_i18n.en
                          : sel.name_i18n.fr;
                      })()}
                    </span>
                    <span aria-hidden className="ml-1 opacity-60">
                      ▾
                    </span>
                  </button>
                  {assignPickerOpen && (
                    <Command
                      className="absolute z-50 mt-1 w-full rounded-md border bg-popover text-sm shadow-md"
                      filter={(value, search) =>
                        value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0
                      }
                    >
                      <Command.Input
                        placeholder={t('players:assign.composer_placeholder')}
                        autoFocus
                        className="w-full border-b bg-transparent px-3 py-2 text-sm outline-none"
                        data-testid="player-assign-composer-search"
                      />
                      <Command.List className="max-h-56 overflow-y-auto p-1">
                        <Command.Empty className="px-3 py-2 text-sm text-muted-foreground">
                          —
                        </Command.Empty>
                        {(composersQuery.data?.items ?? []).map((c) => {
                          const label =
                            lang === 'en' && c.name_i18n.en
                              ? c.name_i18n.en
                              : c.name_i18n.fr;
                          return (
                            <Command.Item
                              key={c.id}
                              value={label}
                              onSelect={() => {
                                setAssignComposerId(c.id);
                                setAssignPickerOpen(false);
                              }}
                              data-testid={`player-assign-composer-option-${c.id}`}
                              className="cursor-pointer rounded px-3 py-2 aria-selected:bg-accent aria-selected:text-accent-foreground"
                            >
                              {label}
                            </Command.Item>
                          );
                        })}
                      </Command.List>
                    </Command>
                  )}
                </div>
              </label>
              <footer className="mt-2 flex items-center justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setAssignPickerOpen(false);
                    setAssignOpen(false);
                  }}
                  disabled={assignMutation.isPending || assignChecking}
                  data-testid="player-assign-cancel"
                >
                  {t('players:assign.cancel')}
                </Button>
                <Button
                  type="submit"
                  disabled={
                    assignComposerId === '' ||
                    assignMutation.isPending ||
                    assignChecking
                  }
                  data-testid="player-assign-submit"
                >
                  {t('players:assign.submit')}
                </Button>
              </footer>
            </form>
          </div>
        </div>
      )}

      {mismatchOpen && (
        <div
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="assign-mismatch-title"
          data-testid="assign-mismatch-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
        >
          <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-lg">
            <h2 id="assign-mismatch-title" className="text-lg font-semibold">
              {t('players:assign_mismatch.title')}
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              {t('players:assign_mismatch.body')}
            </p>
            <form
              className="mt-4 flex flex-col gap-4"
              onSubmit={(e) => {
                e.preventDefault();
                handleForceAssign();
              }}
            >
              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium">
                  {t('players:assign_mismatch.reason_label')}
                </span>
                <Input
                  type="text"
                  required
                  minLength={3}
                  value={mismatchReason}
                  onChange={(e) => setMismatchReason(e.target.value)}
                  placeholder={t('players:assign_mismatch.reason_placeholder')}
                  disabled={assignMutation.isPending}
                  data-testid="assign-mismatch-reason"
                />
              </label>
              <footer className="mt-2 flex items-center justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setMismatchOpen(false);
                    setMismatchReason('');
                    setPendingComposerId('');
                  }}
                  disabled={assignMutation.isPending}
                  data-testid="assign-mismatch-cancel"
                >
                  {t('players:assign_mismatch.cancel')}
                </Button>
                <Button
                  type="submit"
                  variant="destructive"
                  disabled={
                    mismatchReason.trim().length < 3 ||
                    assignMutation.isPending
                  }
                  data-testid="assign-force-submit"
                >
                  {t('players:assign_mismatch.force_submit')}
                </Button>
              </footer>
            </form>
          </div>
        </div>
      )}

      {/* navigate hook is used for back if needed */}
      <span data-testid="detail-navigate-sentinel" hidden onClick={() => navigate('/players')} />
    </div>
  );
}


interface SectionTitleProps {
  id?: string;
  icon: IconName;
  children: ReactNode;
}

/** Card heading with a leading icon — the 2026 section-title pattern. */
function SectionTitle({ id, icon, children }: SectionTitleProps): JSX.Element {
  return (
    <CardHeader>
      <CardTitle id={id} className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
        <Icon name={icon} size={16} aria-hidden />
        {children}
      </CardTitle>
    </CardHeader>
  );
}

interface MetaRowProps {
  label: string;
  value: string;
  testId?: string;
}

function MetaRow({ label, value, testId }: MetaRowProps): JSX.Element {
  return (
    <div className="flex flex-col">
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="text-sm" data-testid={testId}>
        {value}
      </dd>
    </div>
  );
}

function formatDateTime(iso: string, lang: Locale): string {
  return formatDate(iso, lang);
}

export default PlayerDetailPage;
